import type {
    CheckoutAddressesData,
    CheckoutJourney,
    CheckoutShippingData,
} from '~~/layers/base/types/checkout'
import {
    type AddressLikeForCheckout,
    type ProfileNameFields,
    mapAddressLikeToCheckoutCartAddress,
} from '~~/layers/base/utils/mapAddressLikeToCheckoutCartAddress'

/**
 * Client-side shipping preview before `finalize-cart` returns.
 * Labels/codes must stay aligned with
 * `handwritingCheckoutAccsDefaults.resolveHandwritingFinalizeShipping`.
 */
export function resolveOptimisticCheckoutShipping(
    journey: CheckoutJourney | null | undefined
): CheckoutShippingData | null {
    if (journey === 'handwritten_note' || journey === 'handwritten_note_bulk') {
        return {
            selectedMethodCode: 'handwriting_stationery',
            methods: [
                {
                    code: 'handwriting_stationery',
                    label: 'Crane Handwritten Note',
                    amount: null,
                },
            ],
        }
    }

    if (journey === 'handwriting_stationery') {
        return {
            selectedMethodCode: 'handwriting_stationery',
            methods: [
                {
                    code: 'handwriting_stationery',
                    label: 'Crane Handwriting Stationery',
                    amount: null,
                },
            ],
        }
    }

    return null
}

export type OptimisticCheckoutAddressSources = {
    contactName?: string | null
    profileNameFallback?: ProfileNameFields | null
    recipientAddress?: AddressLikeForCheckout | null
    returnAddress?: AddressLikeForCheckout | null
}

/**
 * Builds checkout address preview from personalize Pinia data (no ACCS round-trip).
 * Returns null when neither shipping nor billing can be mapped.
 */
export function resolveOptimisticCheckoutAddresses(
    sources: OptimisticCheckoutAddressSources
): CheckoutAddressesData | null {
    const shippingAddress = sources.recipientAddress
        ? mapAddressLikeToCheckoutCartAddress(sources.recipientAddress, {
              nameFallback: sources.contactName,
          })
        : null

    const billingAddress = sources.returnAddress
        ? mapAddressLikeToCheckoutCartAddress(sources.returnAddress, {
              profileNameFallback: sources.profileNameFallback,
          })
        : null

    if (!shippingAddress && !billingAddress) {
        return null
    }

    return {
        shippingAddress,
        billingAddress,
    }
}
